import React from 'react'
import { TyreFitmentCentre } from '../../../data/tyreFitmentCentres'
import { useBookmarks } from '../../../hooks/useBookmarks'
import { useAuthStore } from '../../../stores/authStore'

interface ShopCardProps {
  shop: TyreFitmentCentre
  onViewShop?: (shop: TyreFitmentCentre) => void
  onCallShop?: (shop: TyreFitmentCentre) => void
  onGetDirections?: (shop: TyreFitmentCentre) => void
}

const ShopCard: React.FC<ShopCardProps> = ({
  shop,
  onViewShop,
  onCallShop,
  onGetDirections
}) => {
  const { user } = useAuthStore();
  const { isBookmarked, toggleBookmark } = useBookmarks();

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    if (!user) {
      alert('Please log in to bookmark shops');
      return;
    }
    // Use shop.id if available, otherwise use shop.name as fallback
    const shopId = shop.id || shop.name;
    toggleBookmark('shop', shopId);
  };

  const shopId = shop.id || shop.name;
  const isShopBookmarked = user ? isBookmarked('shop', shopId) : false;
  const isOpenNow = () => {
    if (shop.is_online) return true // online stores are always open
    const now = new Date()
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()

    const todayHours = shop.openingHours[currentDay]
    if (!todayHours || todayHours === 'Closed') return false

    // Handle different formats: "8:00 AM - 5:30 PM" or "08:00-17:00"
    let openTime: string = ''
    let closeTime: string = ''

    if (todayHours.includes(' - ')) {
      // 12-hour format: "8:00 AM - 5:30 PM"
      const timeRange = todayHours.split(' - ')
      if (timeRange.length !== 2) return false
      openTime = timeRange[0]
      closeTime = timeRange[1]
    } else if (todayHours.includes('-')) {
      // 24-hour format: "08:00-17:00"
      const timeRange = todayHours.split('-')
      if (timeRange.length !== 2) return false
      openTime = timeRange[0]
      closeTime = timeRange[1]
    } else {
      return false
    }

    // Parse open time
    const openDate = parseTimeString(openTime.trim(), now)
    if (!openDate) return false

    // Parse close time
    const closeDate = parseTimeString(closeTime.trim(), now)
    if (!closeDate) return false

    return now >= openDate && now <= closeDate
  }

  const parseTimeString = (timeStr: string, baseDate: Date): Date | null => {
    // Try 12-hour format first: "8:00 AM" or "8:00 PM"
    const ampmMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (ampmMatch) {
      let [, hourStr, minuteStr, period] = ampmMatch
      let hour = parseInt(hourStr, 10)
      const minute = parseInt(minuteStr, 10)

      if (period.toUpperCase() === 'PM' && hour !== 12) hour += 12
      if (period.toUpperCase() === 'AM' && hour === 12) hour = 0

      const date = new Date(baseDate)
      date.setHours(hour, minute, 0, 0)
      return date
    }

    // Try 24-hour format: "08:00" or "17:00"
    const hour24Match = timeStr.match(/^(\d{1,2}):(\d{2})$/)
    if (hour24Match) {
      const [, hourStr, minuteStr] = hour24Match
      const hour = parseInt(hourStr, 10)
      const minute = parseInt(minuteStr, 10)

      const date = new Date(baseDate)
      date.setHours(hour, minute, 0, 0)
      return date
    }

    return null
  }

  // Render the round-the-clock sentinel nicely; pass everything else through.
  const formatHoursValue = (v?: string) => {
    if (!v) return 'Closed'
    const val = v.trim()
    if (val === '00:00-24:00' || val === '00:00-23:59') return 'Open 24 hours'
    return val
  }

  const getTodayHours = () => {
    if (shop.is_online) return 'Open 24 hours'
    const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    return formatHoursValue(shop.openingHours[currentDay] || 'Closed')
  }

  return (
    <div
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group cursor-pointer"
      onClick={() => {
        // Generate slug from shop.name (kebab-case, URL-safe)
        const slug = shop.slug || shop.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        window.location.href = `/business/${slug}`;
      }}
      tabIndex={0}
      role="button"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          const slug = shop.slug || shop.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          window.location.href = `/business/${slug}`;
        }
      }}
    >
      {/* Shop Image */}
      <div
        className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center relative"
        style={{
          backgroundImage: "url('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSzbHl-OMtpHNaeZBw5SPT5fRM39ZWt9oep-g&s')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute top-3 left-3 z-20">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            isOpenNow()
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {isOpenNow() ? 'Open Now' : 'Closed'}
          </span>
        </div>
        {/* Bookmark Button */}
        <div className="absolute top-3 right-3 z-20 flex items-center space-x-2">
          {shop.is_online ? (
            <span className="bg-white text-blue-700 px-2 py-1 rounded-full text-xs font-medium shadow">
              Online store
            </span>
          ) : (shop.distance !== undefined && shop.distance !== null && (
            <span className="bg-white text-gray-700 px-2 py-1 rounded-full text-xs font-medium shadow">
              {shop.distance}km away
            </span>
          ))}
          {user && (
            <button
              onClick={handleBookmarkClick}
              className={`p-2 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center ${
                isShopBookmarked
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-blue-500'
              }`}
              title={isShopBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
            >
              <svg className="w-4 h-4" fill={isShopBookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
              </svg>
            </button>
          )}
        </div>
        <div className="text-center z-10 w-full">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 overflow-hidden shadow-lg border-4 border-white">
            {(shop.logo_url && shop.logo_url.trim().length > 0) ? (
              <img
                src={shop.logo_url}
                alt={shop.name}
                className="object-cover w-20 h-20"
                onError={(e) => {
                  console.error('Image failed to load:', shop.name, shop.logo_url);
                  e.currentTarget.onerror = null;
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            )}
          </div>
          <h3 className="text-2xl font-extrabold text-white drop-shadow-lg">{shop.name}</h3>
        </div>
        <div className="absolute inset-0 bg-black bg-opacity-10 pointer-events-none" />
      </div>

      <div className="p-4">
        {/* Shop Name */}
        <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
          {shop.name}
        </h3>

        {/* Address */}
        <div className="flex items-start mb-3">
          <svg className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-gray-600">
            {shop.is_online ? (
              <>
                <div>Online store · ships nationwide</div>
                {shop.website && (
                  <a
                    href={/^https?:\/\//.test(shop.website) ? shop.website : `https://${shop.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-blue-600 hover:underline break-all"
                  >
                    {shop.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </>
            ) : (
              <>
                <div>{shop.address}</div>
                <div>{shop.city}, {shop.province}</div>
              </>
            )}
          </div>
        </div>

        {/* Rating */}
        <div className="flex items-center mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <svg
                key={i}
                className={`w-4 h-4 ${
                  i < Math.floor(shop.rating) ? 'text-yellow-400' : 'text-gray-300'
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <span className="ml-2 text-sm text-gray-600">
            {shop.rating} ({shop.reviewCount} reviews)
          </span>
        </div>

        {/* Opening Hours */}
        <div className="mb-3">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Today: </span>
            <span className={isOpenNow() ? 'text-green-600' : 'text-red-600'}>
              {getTodayHours()}
            </span>
          </div>
          {shop.estimatedTime && (
            <div className="text-sm text-blue-600">
              <span className="font-medium">Travel time: </span>
              {shop.estimatedTime}
            </div>
          )}
        </div>

        {/* Services */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {shop.services.slice(0, 3).map((service, index) => (
              <span
                key={index}
                className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full"
              >
                {service}
              </span>
            ))}
            {shop.services.length > 3 && (
              <span className="text-xs text-gray-500">+{shop.services.length - 3} more</span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onCallShop?.(shop)}
              className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call
            </button>
            {shop.is_online ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (shop.website) {
                    const url = /^https?:\/\//.test(shop.website) ? shop.website : `https://${shop.website}`
                    window.open(url, '_blank')
                  }
                }}
                className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
                </svg>
                Website
              </button>
            ) : (
              <button
                onClick={() => onGetDirections?.(shop)}
                className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Directions
              </button>
            )}
          </div>
          <button
            onClick={() => onViewShop?.(shop)}
            className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  )
}

export default ShopCard